/**
 * Taking the money.
 *
 * The busiest dialog in the product, so it is built around the two things a
 * cashier actually does: press the note the customer handed over, and read back
 * the change.
 *
 * Split payment is supported because large tables ask for it, but it stays out
 * of the way until it is needed - the default path is one method, one tap,
 * done.
 *
 * The rate can change here. Punjab taxes a card payment at a lower rate than
 * cash, so the total is not final until the method is chosen. The server
 * re-prices on settle and the response is what prints, which is why the dialog
 * shows "the total may change" rather than pretending otherwise.
 */

import { useState, useEffect, useMemo } from 'react';
import { FiCheck, FiPlus, FiTrash2, FiCreditCard, FiSmartphone, FiDollarSign } from 'react-icons/fi';

import Modal from '../ui/Modal';
import NumericKeypad from './NumericKeypad';
import { money } from '../../utils/format';

const ICONS = { CASH: FiDollarSign, CARD: FiCreditCard, WALLET: FiSmartphone };

/**
 * The notes a customer is likely to hand over for a given bill.
 * Rounding up to the next 100, 500, 1000 and 5000 covers almost every real
 * tender and saves typing the exact amount.
 */
const quickAmountsFor = (total) => {
    const suggestions = new Set([Math.ceil(total)]);
    for (const step of [100, 500, 1000, 5000]) {
        const rounded = Math.ceil(total / step) * step;
        if (rounded >= total) suggestions.add(rounded);
    }
    return [...suggestions].sort((a, b) => a - b).slice(0, 4);
};

const PaymentModal = ({
    open, onClose, onConfirm, totalPayable, paymentModes, preferredMode = null, busy = false
}) => {
    // Rows of { paymentModeId, code, name, amount, reference }
    const [rows, setRows] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [entry, setEntry] = useState('');

    // Start with one row for the method the cashier pressed, holding the whole
    // bill. That makes the common case - one method, exact - a single tap.
    useEffect(() => {
        if (!open) return;

        const mode = preferredMode
            || paymentModes.find((m) => m.Payment_Mode_Code === 'CASH')
            || paymentModes[0];

        setRows([{
            paymentModeId: mode.Payment_Mode_ID,
            code: mode.Payment_Mode_Code,
            name: mode.Payment_Mode_Name,
            requiresReference: !!mode.Requires_Reference,
            allowsChange: !!mode.Allows_Change,
            amount: totalPayable,
            tendered: totalPayable,
            reference: ''
        }]);
        setActiveIndex(0);
        setEntry('');
    }, [open, preferredMode, paymentModes, totalPayable]);

    const active = rows[activeIndex] || null;

    const totals = useMemo(() => {
        const paid = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
        const tendered = rows.reduce(
            (sum, row) => sum + (row.allowsChange ? (Number(row.tendered) || 0) : (Number(row.amount) || 0)),
            0
        );
        return {
            paid: Math.round(paid * 100) / 100,
            tendered: Math.round(tendered * 100) / 100,
            remaining: Math.round((totalPayable - paid) * 100) / 100,
            change: Math.round(Math.max(0, tendered - totalPayable) * 100) / 100
        };
    }, [rows, totalPayable]);

    /** Keypad entry applies to the active row. */
    const applyEntry = (raw) => {
        setEntry(raw);
        const value = Number(raw) || 0;

        setRows((current) => current.map((row, index) => {
            if (index !== activeIndex) return row;

            // For cash the number typed is what was handed over, and the amount
            // applied to the bill is capped at what is still owed - handing over
            // Rs. 2000 for a Rs. 1705 bill pays 1705 and returns 295.
            if (row.allowsChange) {
                const owed = totalPayable - current.reduce(
                    (sum, r, i) => (i === index ? sum : sum + (Number(r.amount) || 0)), 0
                );
                return { ...row, tendered: value, amount: Math.min(value, Math.max(0, owed)) };
            }

            return { ...row, amount: value, tendered: value };
        }));
    };

    const addRow = (mode) => {
        const remaining = Math.max(0, totals.remaining);
        setRows((current) => [...current, {
            paymentModeId: mode.Payment_Mode_ID,
            code: mode.Payment_Mode_Code,
            name: mode.Payment_Mode_Name,
            requiresReference: !!mode.Requires_Reference,
            allowsChange: !!mode.Allows_Change,
            amount: remaining,
            tendered: remaining,
            reference: ''
        }]);
        setActiveIndex(rows.length);
        setEntry('');
    };

    const removeRow = (index) => {
        setRows((current) => current.filter((_, i) => i !== index));
        setActiveIndex(0);
        setEntry('');
    };

    const changeMode = (index, mode) => {
        setRows((current) => current.map((row, i) => (i === index ? {
            ...row,
            paymentModeId: mode.Payment_Mode_ID,
            code: mode.Payment_Mode_Code,
            name: mode.Payment_Mode_Name,
            requiresReference: !!mode.Requires_Reference,
            allowsChange: !!mode.Allows_Change
        } : row)));
    };

    const missingReference = rows.some((row) => row.requiresReference && !row.reference.trim());
    // A paisa of float noise must not block a cashier from closing a bill.
    const isShort = totals.remaining > 0.01;
    const canConfirm = !isShort && !missingReference && !busy && rows.length > 0;

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Take payment"
            subtitle={`Total payable Rs. ${money(totalPayable)}`}
            width="lg"
            dismissible={!busy}
            footer={(
                <>
                    <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn-success min-w-[13rem]"
                        disabled={!canConfirm}
                        onClick={() => onConfirm(rows.map((row) => ({
                            paymentModeId: row.paymentModeId,
                            amount: row.amount,
                            tenderedAmount: row.allowsChange ? row.tendered : row.amount,
                            referenceNo: row.reference.trim() || null
                        })))}
                    >
                        <FiCheck className="h-5 w-5" />
                        {busy ? 'Settling…'
                            : isShort ? `Short Rs. ${money(totals.remaining)}`
                                : missingReference ? 'Enter the reference'
                                    : `Complete · Rs. ${money(totals.paid)}`}
                    </button>
                </>
            )}
        >
            <div className="grid gap-5 md:grid-cols-[1fr_auto]">
                {/* Left: the payment rows */}
                <div className="min-w-0 space-y-3">
                    {rows.map((row, index) => {
                        const Icon = ICONS[row.code] || FiCreditCard;
                        const isActive = index === activeIndex;

                        return (
                            <div
                                key={index}
                                onClick={() => { setActiveIndex(index); setEntry(''); }}
                                className={`cursor-pointer rounded-xl border p-3 transition-colors
                                            ${isActive ? 'border-brand-500 bg-brand-50/50 ring-1 ring-brand-400' : 'border-slate-200 bg-white'}`}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon className="h-4 w-4 shrink-0 text-slate-500" />

                                    <select
                                        className="input h-10 flex-1 py-0"
                                        value={row.paymentModeId}
                                        onChange={(event) => {
                                            const mode = paymentModes.find(
                                                (m) => m.Payment_Mode_ID === Number(event.target.value)
                                            );
                                            if (mode) changeMode(index, mode);
                                        }}
                                        onClick={(event) => event.stopPropagation()}
                                    >
                                        {paymentModes.map((mode) => (
                                            <option key={mode.Payment_Mode_ID} value={mode.Payment_Mode_ID}>
                                                {mode.Payment_Mode_Name}
                                            </option>
                                        ))}
                                    </select>

                                    <div className="w-32 shrink-0 text-right">
                                        <p className="tabular text-lg font-semibold text-slate-800">
                                            {money(row.allowsChange ? row.tendered : row.amount)}
                                        </p>
                                        {row.allowsChange && row.tendered !== row.amount && (
                                            <p className="tabular text-xs text-slate-500">
                                                applied {money(row.amount)}
                                            </p>
                                        )}
                                    </div>

                                    {rows.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={(event) => { event.stopPropagation(); removeRow(index); }}
                                            className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                                            aria-label="Remove this payment"
                                        >
                                            <FiTrash2 className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>

                                {row.requiresReference && (
                                    <input
                                        className={`input mt-2 ${!row.reference.trim() ? 'border-amber-400' : ''}`}
                                        placeholder={`${row.name} reference / slip number`}
                                        value={row.reference}
                                        onChange={(event) => setRows((current) => current.map(
                                            (r, i) => (i === index ? { ...r, reference: event.target.value } : r)
                                        ))}
                                        onClick={(event) => event.stopPropagation()}
                                    />
                                )}
                            </div>
                        );
                    })}

                    {/* Split */}
                    {totals.remaining > 0.01 && (
                        <div className="flex flex-wrap gap-2">
                            <span className="self-center text-sm text-slate-500">Split with:</span>
                            {paymentModes
                                .filter((mode) => !rows.some((row) => row.paymentModeId === mode.Payment_Mode_ID))
                                .map((mode) => (
                                    <button
                                        key={mode.Payment_Mode_ID}
                                        type="button"
                                        onClick={() => addRow(mode)}
                                        className="btn-secondary btn-sm"
                                    >
                                        <FiPlus className="h-3.5 w-3.5" /> {mode.Payment_Mode_Name}
                                    </button>
                                ))}
                        </div>
                    )}

                    {/* The summary a cashier reads out loud */}
                    <dl className="rounded-xl bg-slate-50 p-3 text-sm">
                        <div className="flex justify-between py-0.5">
                            <dt className="text-slate-600">Total payable</dt>
                            <dd className="tabular font-medium">{money(totalPayable)}</dd>
                        </div>
                        <div className="flex justify-between py-0.5">
                            <dt className="text-slate-600">Received</dt>
                            <dd className="tabular font-medium">{money(totals.tendered)}</dd>
                        </div>

                        {isShort ? (
                            <div className="mt-1 flex justify-between border-t border-slate-200 pt-1.5">
                                <dt className="font-semibold text-red-700">Still to pay</dt>
                                <dd className="tabular text-lg font-bold text-red-700">
                                    {money(totals.remaining)}
                                </dd>
                            </div>
                        ) : (
                            <div className="mt-1 flex justify-between border-t border-slate-200 pt-1.5">
                                <dt className="font-semibold text-emerald-700">Change to give</dt>
                                <dd className="tabular text-lg font-bold text-emerald-700">
                                    {money(totals.change)}
                                </dd>
                            </div>
                        )}
                    </dl>

                    <p className="text-xs text-slate-500">
                        The tax rate can depend on how the bill is paid, so the final total is
                        confirmed by the server when the payment is completed.
                    </p>
                </div>

                {/* Right: the keypad, applying to whichever row is active */}
                <div className="w-full md:w-64">
                    <div className="mb-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-right">
                        <p className="text-xs text-slate-500">
                            {active?.allowsChange ? 'Cash received' : `${active?.name || ''} amount`}
                        </p>
                        <p className="tabular text-2xl font-semibold text-slate-800">
                            {entry || money(active?.allowsChange ? active?.tendered : active?.amount)}
                        </p>
                    </div>

                    <NumericKeypad
                        value={entry}
                        onChange={applyEntry}
                        quickAmounts={active?.allowsChange ? quickAmountsFor(totalPayable) : null}
                        disabled={busy}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default PaymentModal;
